import { Box, Grid, Text } from "@chakra-ui/react"
import { Topic } from "../../views/journey"
import Tile from "../tile"
import React, {
  CSSProperties,
  ReactNode,
  useContext,
  useEffect,
  useState
} from "react"
import useEase from "../hooks/useEase"
import LeftArrow from "../../common/icons/left-arrow"
import { MaybeReactScrollMaybeNative } from "../../common/styledScrollableList"
import { useHistory, useLocation } from "react-router-dom"
import CircleTransition from "../circleTransition"
import LoadingView from "../../views/loading"
import { ActiveTopic, subTopicSelector } from "../../../recoil/atoms/lessons"
import { useRecoilState, useSetRecoilState } from "recoil"
import { journeyUiSelector } from "../../../recoil/atoms/journeyUI"
import fitty from "fitty"
import LogoLoader from "src/components/views/loading/logoLoader"
import { useIsMobileApp } from "src/util/deviceType"
import HomeBackIcon from "src/components/views/home/HomeBackIcon"

type Props = {
  topics: Topic[]
  drillDown?: (activeTopic: ActiveTopic) => void
  menuItemsDeselected: boolean
}

function TopicJourney({
  topics,
  drillDown = () => null,
  menuItemsDeselected
}: Props) {
  const lowestScore = Math.min(...topics.map(topic => topic.progress?.new))
  const firstIndexOfLowest = topics.findIndex(
    topic => topic.progress?.new === lowestScore
  )
  const isMobileApp = useIsMobileApp()

  return (
    <Grid
      width="100%"
      minH="100vh"
      height="fit-content"
      backgroundColor="#0D0A04"
      gridTemplateRows={`repeat(${topics.length}, 25vh)`}
      justifyContent="center"
    >
      {isMobileApp && (
        <HomeBackIcon
          JoinPageButtonMouseoverTextColor={"black"}
          JoinPageButtonTextColor={"black"}
          joinButtonBackgroundColor={"white"}
          joinButtonBorderColor={"black"}
          joinButtonMouseoverBackgroundColor={"#eee"}
          navigatedFromiOS={isMobileApp}
        />
      )}

      {topics.map((topic, i) => (
        <Tile
          key={topic.id}
          {...topic}
          index={i}
          drillDown={drillDown}
          menuItemsDeselected={menuItemsDeselected}
          showTooltip={firstIndexOfLowest === i}
        />
      ))}
    </Grid>
  )
}

type SubTopicsProps = {
  topics: Topic[]
  background?: string
  onClose: () => void
  title: string
  topicId: number
  personalRoomHash: string
}

TopicJourney.SubTopics = ({
  topics,
  background,
  onClose,
  title,
  personalRoomHash
}: SubTopicsProps) => {
  const [style, setStyle] = useState<CSSProperties>({})
  const [activeTopic, setActiveTopic] = useRecoilState(subTopicSelector)

  const setJourneyUi = useSetRecoilState(journeyUiSelector)

  const vw = Math.max(
    document.documentElement.clientWidth || 0,
    window.innerWidth || 0
  )

  const { animationProgress: closeProgress, start: desktopClose } = useEase({
    start: 0,
    end: vw,
    ease: "easeInQuart",
    manualTrigger: true,
    fillMode: "persist",
    speed: 3,
    onEnd: onClose
  })

  useEffect(() => {
    setStyle({
      transform: `translateX(${closeProgress.ease * -1}px)`
    })
  }, [closeProgress])

  useEffect(() => {
    fitty("#subtopic-title", { multiLine: true })
  }, [])

  return (
    <>
      <MaybeReactScrollMaybeNative
        position="fixed"
        top={0}
        width="100%"
        height="100svh"
        overflowX="hidden"
        background={background || "transparent"}
        style={style}
        trackColor={background}
        thumbColor="black"
      >
        <Box
          width="45%"
          fontSize={{ base: "22px", sm: "3xl", md: "4xl", lg: "5xl" }}
          lineHeight="1.1em"
          color="white"
          opacity="0.8"
          fontFamily="Poppins"
          position="absolute"
          top="50%"
          left={{ base: "1rem", sm: "2rem" }}
          transform="translate(0, -50%)"
        >
          <Text id="subtopic-title" lineHeight="1rem">
            {title}
          </Text>
        </Box>

        <Box
          position="absolute"
          top={{ base: "1rem", sm: "2rem" }}
          left={{ base: "1rem", sm: "2rem" }}
          display="flex"
          flexDirection="row"
          alignItems="center"
          color="white"
        >
          <LeftArrow />
          <Text
            fontFamily="Poppins"
            fontSize="14px"
            fontWeight="bold"
            cursor="pointer"
            onClick={desktopClose}
          >
            Back to topics
          </Text>
        </Box>

        <Box
          display="grid"
          gridTemplateRows={`repeat(${topics.length}, 25vh)`}
          justifyContent="center"
          alignItems="center"
          width="100%"
          minH="100vh"
          height="fit-content"
        >
          {topics.map((topic, i) => (
            <Tile
              key={topic.id}
              {...topic}
              index={i}
              drillDown={setActiveTopic}
              isBlack={true}
            />
          ))}
        </Box>
      </MaybeReactScrollMaybeNative>
      {activeTopic && (
        <CircleTransition
          position={{
            x: activeTopic.centerPos.x,
            y: activeTopic.centerPos.y
          }}
          color={"#000000"}
        >
          {animationState => {
            if (animationState !== "FINISHED") return

            return (
              <JourneyRedirect
                transition
                onEnd={() => {
                  setJourneyUi({
                    transition: "subtopicsToLessons",
                    externalRoute: `/${personalRoomHash}`
                  })
                }}
              >
                <LogoLoader />
              </JourneyRedirect>
            )
          }}
        </CircleTransition>
      )}
    </>
  )
}

export function JourneyRedirect({
  transition = false,
  onEnd = () => null,
  children
}: {
  transition?: boolean
  onEnd?: () => void
  children: ReactNode
}) {
  const { animationProgress } = useEase({
    start: 0,
    end: 1,
    ease: "easeInSine",
    speed: 2,
    fillMode: "persist",
    onEnd
  })

  return (
    <Box
      position="absolute"
      top={0}
      left={0}
      width="100%"
      height="100vh"
      background="#000000"
    >
      {transition ? (
        <Box opacity={animationProgress.ease}>{children}</Box>
      ) : (
        <>{children}</>
      )}
    </Box>
  )
}

export default TopicJourney
